import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SegmentedControl, Switch } from '@/components/ui/controls';
import { NumberField, SelectField, SliderField } from '@/components/ui/fields';
import { SIZE_PRESET_OPTIONS, SPEED_OPTIONS } from '@/constants/media';
import { useProjectStore } from '@/store/projectStore';
import { settingsSchema, type SettingsFormValues } from './settingsSchema';

function SectionLabel({ children }: { children: string }) {
  return <h3 className="eyebrow">{children}</h3>;
}

export function SettingsPanel() {
  const settings = useProjectStore((state) => state.settings);
  const updateSettings = useProjectStore((state) => state.updateSettings);
  const phase = useProjectStore((state) => state.conversion.phase);
  const busy = phase === 'loading-engine' || phase === 'palette' || phase === 'encoding';

  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsSchema),
    mode: 'onChange',
    defaultValues: settings,
  });

  // Live-sync: every keystroke runs through the schema; only valid, parsed
  // values reach the store, so the conversion plan can trust its inputs.
  useEffect(() => {
    const subscription = watch((values) => {
      const parsed = settingsSchema.safeParse(values);
      if (parsed.success) updateSettings(parsed.data);
    });
    return () => subscription.unsubscribe();
  }, [watch, updateSettings]);

  const sizePreset = watch('sizePreset');
  const lockAspect = watch('lockAspect');
  const fps = watch('fps');

  return (
    <section aria-label="GIF settings" className="panel p-4">
      <fieldset disabled={busy} className="space-y-5 disabled:opacity-60">
        <div className="space-y-3">
          <SectionLabel>Output size</SectionLabel>
          <SelectField label="Width" {...register('sizePreset')}>
            {SIZE_PRESET_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectField>

          {sizePreset === 'custom' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <NumberField
                  label="Width"
                  suffix="px"
                  min={16}
                  max={1920}
                  error={errors.customWidth?.message}
                  {...register('customWidth')}
                />
                <NumberField
                  label="Height"
                  suffix="px"
                  min={16}
                  max={1920}
                  disabled={lockAspect}
                  error={errors.customHeight?.message}
                  {...register('customHeight')}
                />
              </div>
              <Controller
                control={control}
                name="lockAspect"
                render={({ field }) => (
                  <Switch
                    label="Keep aspect ratio"
                    hint="Height follows the (cropped) frame shape."
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          )}
        </div>

        <div className="space-y-3">
          <SectionLabel>Motion</SectionLabel>
          <SliderField
            label="Frame rate"
            valueLabel={`${fps} fps`}
            min={1}
            max={50}
            step={1}
            error={errors.fps?.message}
            {...register('fps')}
          />
          <SelectField label="Playback speed" {...register('speed')}>
            {SPEED_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectField>
        </div>

        <div className="space-y-3">
          <SectionLabel>Quality</SectionLabel>
          <Controller
            control={control}
            name="quality"
            render={({ field }) => (
              <SegmentedControl
                label="Palette"
                value={field.value}
                onChange={field.onChange}
                options={[
                  { value: 'high', label: 'High', hint: '256 colors, up to 640 px on Auto' },
                  {
                    value: 'balanced',
                    label: 'Balanced',
                    hint: '160 colors, up to 480 px on Auto',
                  },
                  { value: 'compact', label: 'Compact', hint: '96 colors, up to 360 px on Auto' },
                ]}
              />
            )}
          />
          <SelectField
            label="Dithering"
            title="How intermediate shades are simulated with a limited palette"
            {...register('dither')}
          >
            <option value="auto">Auto (recommended)</option>
            <option value="floyd">Floyd–Steinberg — smoothest gradients</option>
            <option value="bayer">Bayer — smaller files</option>
            <option value="none">None — flat art & screencasts</option>
          </SelectField>
          <Controller
            control={control}
            name="dropDuplicates"
            render={({ field }) => (
              <Switch
                label="Collapse duplicate frames"
                hint="Still moments become one longer frame — smaller files."
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        <div className="space-y-3">
          <SectionLabel>Playback</SectionLabel>
          <Controller
            control={control}
            name="loopForever"
            render={({ field }) => (
              <Switch
                label="Loop forever"
                hint="Off = the GIF plays once and stops."
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </fieldset>
    </section>
  );
}
